import { useMemo } from 'react';
import { useArbiterClient } from '../context/ArbiterClientContext';
import { usePaginatedApi } from './usePaginatedApi';
import type { EvaluationSummary, ListEvaluationsInput } from '@amzn/helianthus-arbiter-client';

interface UseEvaluationsResult {
  evaluations: EvaluationSummary[];
  loading: boolean;
  fetchingMore: boolean;
  error: string | null;
  refetch: () => void;
}

export function useEvaluations(params: Omit<ListEvaluationsInput, 'continuationToken'> = {}): UseEvaluationsResult {
  const client = useArbiterClient();

  // Stabilize params to prevent usePaginatedApi refetch on every render.
  // sortOrder: DESC ensures the first page contains the most recent evaluations,
  // giving users immediate visibility while remaining pages load in the background.
  const stableParams = useMemo(
    () => ({
      resourceType: params.resourceType,
      resourceId: params.resourceId,
      bindleLock: params.bindleLock,
      completed: params.completed,
      sortOrder: 'DESC' as const,
      limit: params.limit,
    }),
    [params.resourceType, params.resourceId, params.bindleLock, params.completed, params.limit],
  );

  const { data, loading, fetchingMore, error, refetch } = usePaginatedApi(
    (fetchParams) =>
      client.listEvaluations(fetchParams).then((r) => ({
        items: r.evaluations ?? [],
        continuationToken: r.continuationToken,
      })),
    stableParams,
  );

  return { evaluations: data, loading, fetchingMore, error, refetch };
}
